/**
 * Visual effects of the Player's ☀ panel (issue walk-bake#11, C13, C14, C17): Glow (bloom strength),
 * Outdoor exposure (stops, only the HDR background seen through windows) and Motion blur (an on/off
 * button, off by default, and a strength slider), saved per device like the exposure settings. They
 * reach the viewer as URL parameters when a walk opens and through `window.walkbake.set` while it is
 * open; the viewer gets the motion blur as one number, the strength when on and 0 when off. On
 * phone-class devices (the "phone" export preset, or a walk the memory guard judges too heavy) the
 * viewer also renders bloom and motion blur at half resolution (`bloom_half=1`, `blur_half=1`).
 *
 * Pure functions apart from storage, so they run under node:test.
 */

import type { WalkbakeApi } from "./exposureSettings.ts";
import { clampRound, type NumericRange } from "./numeric.ts";
import { deviceGpuLimitMb, needsMemoryWarning } from "./memory.ts";

export interface EffectSettings {
  /** Bloom strength, 0 (off) to 1. */
  glow: number;
  /** Outdoor exposure in stops, -3..+3: scales only the window background (the viewer's `outdoor`). */
  outdoor: number;
  /** Motion blur strength, 0 to 1 (the slider); it applies only while `motionBlurOn`. */
  motionBlur: number;
  /** The motion blur on/off button (C17), off by default. */
  motionBlurOn: boolean;
}

/** The settings as the viewer takes them: numbers, motion blur 0 when it is off. */
export type ViewerEffects = Pick<EffectSettings, "glow" | "outdoor" | "motionBlur">;

export const EFFECT_RANGE: NumericRange = { min: 0, max: 1, step: 0.01 };

export const OUTDOOR_RANGE: NumericRange = { min: -3, max: 3, step: 0.1 };

/** Strength the button turns motion blur on with when the slider is at 0. */
export const DEFAULT_MOTION_BLUR = 0.5;

/** Santi's picks on the house walk (2026-11-03, P105). */
export const DEFAULT_EFFECT_SETTINGS: EffectSettings = { glow: 0.02, outdoor: -0.7, motionBlur: 0.03, motionBlurOn: true };

const RANGES: Record<keyof ViewerEffects, NumericRange> = {
  glow: EFFECT_RANGE,
  outdoor: OUTDOOR_RANGE,
  motionBlur: EFFECT_RANGE,
};

const STORAGE_KEY = "walk-player.effects";

/** Clamped to 0..1 and rounded to the slider step (two decimals). */
export function clampUnit(value: number): number {
  return clampRound(EFFECT_RANGE, value);
}

/** A finite number clamped to the setting's range, or the fallback when the value is not a number. */
function inRange(key: keyof ViewerEffects, value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? clampRound(RANGES[key], value) : fallback;
}

function sanitize(raw: Partial<Record<keyof EffectSettings, unknown>>, fallback: EffectSettings): EffectSettings {
  return {
    glow: inRange("glow", raw.glow, fallback.glow),
    outdoor: inRange("outdoor", raw.outdoor, fallback.outdoor),
    motionBlur: inRange("motionBlur", raw.motionBlur, fallback.motionBlur),
    motionBlurOn: typeof raw.motionBlurOn === "boolean" ? raw.motionBlurOn : fallback.motionBlurOn,
  };
}

/** The motion blur the viewer gets: the strength while the button is on, else 0. */
export function effectiveMotionBlur(settings: EffectSettings): number {
  return settings.motionBlurOn ? settings.motionBlur : 0;
}

/** The button: flips on/off; turning on with the slider at 0 uses DEFAULT_MOTION_BLUR. */
export function toggleMotionBlur(settings: EffectSettings): EffectSettings {
  const on = !settings.motionBlurOn;
  return { ...settings, motionBlurOn: on, motionBlur: on && settings.motionBlur <= 0 ? DEFAULT_MOTION_BLUR : settings.motionBlur };
}

/** The slider: sets the strength; above 0 it also turns motion blur on, at 0 off. */
export function setMotionBlurStrength(settings: EffectSettings, value: number): EffectSettings {
  const motionBlur = clampUnit(value);
  return { ...settings, motionBlur, motionBlurOn: motionBlur > 0 };
}

export function loadEffectSettings(): EffectSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return sanitize(JSON.parse(raw) as Partial<Record<keyof EffectSettings, unknown>>, DEFAULT_EFFECT_SETTINGS);
    }
  } catch {
    // Blocked or corrupt storage: fall back to defaults.
  }
  return { ...DEFAULT_EFFECT_SETTINGS };
}

export function saveEffectSettings(settings: EffectSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Not persisted; still applies for this session.
  }
}

/**
 * Overrides from the Player's own URL (`?glow=0.2&outdoor=1.5&motion_blur=0.5`), for tests. They win over the
 * saved settings for the walk opened in that page, and are not saved. Invalid values are ignored.
 * `motion_blur` above 0 turns motion blur on at that strength; `0` turns it off (the strength stays).
 */
export function withEffectUrlOverrides(settings: EffectSettings, search: string): EffectSettings {
  const params = new URLSearchParams(search);
  const number = (key: string): number | undefined => {
    const value = params.get(key);
    return value === null || value.trim() === "" ? undefined : Number(value);
  };
  const result = sanitize({ glow: number("glow"), outdoor: number("outdoor") }, settings);
  const blur = number("motion_blur");
  if (blur === undefined || !Number.isFinite(blur)) return result;
  return clampUnit(blur) > 0 ? setMotionBlurStrength(result, blur) : { ...result, motionBlurOn: false };
}

/**
 * Whether the viewer should render bloom at half resolution: the "phone" export preset (any case: Walk Bake writes "DESKTOP", "LAPTOP", "TABLET" and "PHONE"), or a walk
 * whose graphics-memory estimate is over this device's limit (the memory guard's own check).
 */
export function bloomHalfFor(
  walk: { preset?: string; gpuMb?: number },
  userAgent: string,
  maxTouchPoints: number,
): boolean {
  return walk.preset?.trim().toLowerCase() === "phone" || needsMemoryWarning(walk.gpuMb, deviceGpuLimitMb(userAgent, maxTouchPoints));
}

/** Device pixels per CSS pixel the viewer draws at on a phone (C16): fixed, never changed while moving. */
export const PHONE_PIXEL_RATIO = 1.5;

/** A phone (not a tablet): iPhone/iPod, or a browser that says "Mobile" (Android phones, Firefox mobile). */
export function isPhone(userAgent: string): boolean {
  return /iPhone|iPod/.test(userAgent) || (/Mobile|Mobi/.test(userAgent) && !/iPad|Tablet/.test(userAgent));
}

/** The viewer's `max_dpr` for this device: PHONE_PIXEL_RATIO on a phone, else null (the viewer's own cap of 2). */
export function maxPixelRatioFor(userAgent: string): number | null {
  return isPhone(userAgent) ? PHONE_PIXEL_RATIO : null;
}

/**
 * Query string for the viewer page: `glow`, `outdoor`, `motion_blur` (0 when off), `bloom_half=1` and
 * `blur_half=1` when `half` (phone-class), and `max_dpr` when `maxPixelRatio` is given.
 */
export function effectQuery(settings: EffectSettings, half: boolean, maxPixelRatio: number | null = null): string {
  const params = new URLSearchParams({
    glow: String(settings.glow),
    outdoor: String(settings.outdoor),
    motion_blur: String(effectiveMotionBlur(settings)),
  });
  if (half) {
    params.set("bloom_half", "1");
    params.set("blur_half", "1");
  }
  if (maxPixelRatio !== null) params.set("max_dpr", String(maxPixelRatio));
  return params.toString();
}

/**
 * What to send the viewer after `patch` changed `settings` (the new ones): the changed numbers, and the
 * effective motion blur when its strength or its button changed.
 */
export function viewerPatch(patch: Partial<EffectSettings>, settings: EffectSettings): Partial<ViewerEffects> {
  return {
    ...(patch.glow !== undefined && { glow: settings.glow }),
    ...(patch.outdoor !== undefined && { outdoor: settings.outdoor }),
    ...((patch.motionBlur !== undefined || patch.motionBlurOn !== undefined) && { motionBlur: effectiveMotionBlur(settings) }),
  };
}

/** Pushes changed effect settings into an open viewer. */
export function applyEffectsToViewer(api: WalkbakeApi | null, patch: Partial<ViewerEffects>): void {
  if (!api) return;
  api.set({
    ...(patch.glow !== undefined && { glow: patch.glow }),
    ...(patch.outdoor !== undefined && { outdoor: patch.outdoor }),
    ...(patch.motionBlur !== undefined && { motionBlur: patch.motionBlur }),
  });
}
